import { IPromise, IQService } from 'angular';
import { IState, IStateParamsService } from 'angular-ui-router';
import * as _ from 'lodash';

import { ImSchool } from 'Src/ng2/shared/services/im-models/im-school';
// tslint:disable-next-line:max-line-length
import { USER_ROLE_PERMISSIONS_FOR_STATES } from '../../../../ng2/shared/constants/user-role-permissions-for-states.constant';
import { UtilitiesService } from '../../../../ng2/shared/services/utilities/utilities.service';
import { ISchool } from '../../../../ng2/shared/typings/interfaces/school.interface';
import { SdcActionsService } from './../../../sdc/flux/sdc-actions.service';
import { ISdcStore } from './../../../sdc/flux/sdc-store.interface';
import { SdcStateService } from './../../../sdc/sdc-state/sdc-state.service';
import {
  IColumnSort,
  IFilter,
  IGridState,
  IProfileState,
  IWizState,
  TValidGridViews,
  TValidSdcKeys,
  TValidSdcViews,
} from './../../../sdc/sdc-state/sdc-state.typings';
import { SpinnerModal } from './../../../shared/modals/status-spinner/status-spinner.service';

/* istanbul ignore next */
export class SdcStateBuilder {
  static buildGridState(sdcKey: TValidSdcKeys): IState {
    const sdcView: TValidSdcViews = 'grid';
    const sdcActivityKey = UtilitiesService.toUpperSnakeCase(sdcKey);

    return {
      // for new routing framework --> url: '/grid?filter&displayColumns'
      url: '/grid?filter&sort&gridView&userAddedColumns',
      template: `

        <new-sdc-grid
          initial-grid-state="$resolve.initialGridState"
          channel="$resolve.channel"
          current-school="$resolve.currentSchool"
          current-user="$resolve.currentUser">
        </new-sdc-grid>

      `,
      reloadOnSearch: false,

      resolve: {
        // This resolve block parses the query params. It shouldn't be injected into the controller; the controller
        // should use initialGridState instead.
        _gridQueryParams(
          $stateParams: IStateParamsService,
          ObjectCache: any,
          UtilitiesService: UtilitiesService,
          $q: IQService,
        ): IPromise<any> {
          const query = {
            filter: $q.resolve(null),
            displayColumns: $q.resolve(null),
            gridView: $q.resolve(null),
            sort: $q.resolve(null),
            userAddedColumns: $q.resolve(null),
          };

          const { filter, sort, gridView, userAddedColumns } = $stateParams;

          if (filter && UtilitiesService.isHash(filter)) {
            query.filter = ObjectCache.getObject(filter);
          }

          if (sort && UtilitiesService.isHash(sort)) {
            query.sort = ObjectCache.getObject(sort);
          }

          if (gridView) {
            query.gridView = $q.resolve(gridView);
          }

          if (userAddedColumns && UtilitiesService.isHash(userAddedColumns)) {
            query.userAddedColumns = ObjectCache.getObject(userAddedColumns);
          }

          return $q.all(query);
        },

        // Resolves the initial grid state. Takes into account the query params, existing values in the store, and the
        // default state for this SDC.
        initialGridState(
          _gridQueryParams,
          SDCStore: ISdcStore,
          sdcActions: SdcActionsService,
          SdcStateService: SdcStateService,
          currentSchool, // Please Do Not Remove: see note @ 450
        ): IGridState {
          const storeGridState = SDCStore.getCurrentSdcState(sdcKey).grid;
          const defaultGridState = SdcStateService[sdcActivityKey];

          const activeFilter = SdcStateBuilder._mergeFilters(
            defaultGridState.filter,
            _gridQueryParams.filter,
            storeGridState.filter,
          );

          const activeSort = SdcStateBuilder._mergeSorts(
            defaultGridState.sort,
            _gridQueryParams.sort,
            storeGridState.sort,
          );

          const activeGridView = SdcStateBuilder._mergeGridViews(
            defaultGridState.gridView,
            _gridQueryParams.gridView,
            storeGridState.gridView,
          );

          const activeUserAddedCols = SdcStateBuilder._mergeUserAddedColumns(
            _gridQueryParams.userAddedColumns,
            storeGridState.userAddedColumns,
          );

          // Update the SDC Store with the merged values
          sdcActions.setCurrentSdcState(sdcKey, sdcView, 'filter', activeFilter);
          sdcActions.setCurrentSdcState(sdcKey, sdcView, 'sort', activeSort);
          sdcActions.setCurrentSdcState(sdcKey, sdcView, 'gridView', activeGridView);
          sdcActions.setCurrentSdcState(sdcKey, sdcView, 'userAddedColumns', activeUserAddedCols);

          return {
            filter: activeFilter,
            sort: activeSort,
            gridView: activeGridView,
            userAddedColumns: activeUserAddedCols,
          };
        },
      },

      data: {
        roles: USER_ROLE_PERMISSIONS_FOR_STATES[`main.school.new-sdc.${sdcKey}.grid`],
      },

      onEnter(
        initialGridState: IGridState,
        sdcActions: SdcActionsService,
        currentSchool: ISchool,
        SpinnerModal,
        SdcStateService: SdcStateService,
      ) {
        const upperKey = UtilitiesService.toUpperSnakeCase(sdcKey);
        const activity = SdcStateService[upperKey];
        const filter = initialGridState.filter;
        const sort = initialGridState.sort;
        const gridView = initialGridState.gridView;
        const userAddedColumns = initialGridState.userAddedColumns;

        return sdcActions.initializeSdcActivityAsync({
          activity,
          filter,
          sort,
          school: currentSchool,
          gridView,
          userAddedColumns,
        });
      },

      onExit(SpinnerModal: SpinnerModal) {
        return SpinnerModal.activateSpinner('Loading...');
      },
    };
  }

  static buildProfileState(sdcKey: TValidSdcKeys): IStateParamsService {
    const sdcView: TValidSdcViews = 'profile';
    const sdcActivityKey = UtilitiesService.toUpperSnakeCase(sdcKey);

    /**
     * TODO: This needs to move to sdc-state-config.constant after it's refactored to include
     * defaultSdcStates property
     *
     * These would then get passed through via the routes themselves
     */
    const defaultSort: IColumnSort[] = [{ colId: 'studentName', sort: 'asc' }];

    return {
      url: '/profile?filter&sort',
      data: {
        roles: USER_ROLE_PERMISSIONS_FOR_STATES[`main.school.new-sdc.${sdcKey}.profile`],
      },
      controller: ($state, initialProfileState, ObjectCache) => {
        /****************************************************
         *  TODO: This is temporary for MVP.
         *  Eventually we want to convert SSV to component and load here
         *  For now, convert sort & filter BACK to hash values and pass thru
         *  to existing student route
         ********************************************************************/
        const tempParams = {
          filter: ObjectCache.cacheObject(initialProfileState.filter),
          sort: ObjectCache.cacheObject(initialProfileState.sort),
        };
        $state.go('main.school.single-student-select-first-student', tempParams);
      },
      resolve: {
        // This resolve block parses the query params. It shouldn't be injected into the controller; the controller
        // should use initialGridState instead.
        _profileQueryParams(
          $stateParams: IStateParamsService,
          ObjectCache: any,
          UtilitiesService: UtilitiesService,
          $q: IQService,
        ): IPromise<any> {
          const query = {
            filter: $q.resolve(null),
            sort: $q.resolve(null),
          };

          if ($stateParams.filter && UtilitiesService.isHash($stateParams.filter)) {
            query.filter = ObjectCache.getObject($stateParams.filter);
          }

          if ($stateParams.sort && UtilitiesService.isHash($stateParams.sort)) {
            query.sort = ObjectCache.getObject($stateParams.sort);
          }

          return $q.all(query);
        },

        // Resolves the initial profile state.
        // Takes into account the query params, existing values in the store, and the
        // default state for this SDC.
        initialProfileState(
          _profileQueryParams,
          SDCStore: ISdcStore,
          sdcActions: SdcActionsService,
          SdcStateService: SdcStateService,
          currentSchool, // Please Do Not Remove: see note @ 450
        ): IProfileState {
          const storeProfileState = SDCStore.getCurrentSdcState(sdcKey).profile;
          const defaultProfileState = SdcStateService[sdcActivityKey];

          const activeFilter = SdcStateBuilder._mergeFilters(
            defaultProfileState.filter,
            _profileQueryParams.filter,
            storeProfileState.filter,
          );

          const activeSort = SdcStateBuilder._mergeSorts(
            defaultProfileState.sort || defaultSort,
            _profileQueryParams.sort,
            storeProfileState.sort,
          );

          // Update the SDC Store with the merged values
          sdcActions.setCurrentSdcState(sdcKey, sdcView, 'filter', activeFilter);
          sdcActions.setCurrentSdcState(sdcKey, sdcView, 'sort', activeSort);

          return {
            filter: activeFilter,
            sort: activeSort,
          };
        },
      },
      onEnter(
        initialProfileState: IProfileState,
        sdcActions: SdcActionsService,
        currentSchool: ISchool,
        SpinnerModal,
        SdcStateService,
      ) {
        const upperKey = UtilitiesService.toUpperSnakeCase(sdcKey);
        const activity = SdcStateService[upperKey];
        const filter = initialProfileState.filter;
        const sort = initialProfileState.sort;
        const gridView = null;

        return sdcActions.initializeSdcActivityAsync({
          activity,
          filter,
          sort,
          school: currentSchool,
          gridView,
        });
      },
    };
  }

  static builGradPlanningWizardState(sdcKey: TValidSdcKeys): IState {
    const stateObj = {
      template: `
        <grad-planning-wizard-transfer
          initial-wizard-state="$resolve.initialWizardState"
          current-school="$resolve.currentSchool"
          current-user="$resolve.currentUser"
          ng-if="$ctrl.isTransfer">
        </grad-planning-wizard-transfer>

        <grad-planning-wizard
          initial-wizard-state="$resolve.initialWizardState"
          current-school="$resolve.currentSchool"
          current-user="$resolve.currentUser"
          ng-if="!$ctrl.isTransfer">
        </grad-planning-wizard>`,

      controller(ImSchool: ImSchool, currentSchool) {
        this.isTransfer = ImSchool.isTransferSchool(currentSchool);
      },
      controllerAs: '$ctrl',
    };
    const genericWizardState = this.buildSharedWizardState(sdcKey);
    return Object.assign(stateObj, genericWizardState);
  }

  static buildRegentsPlanningWizardState(sdcKey: TValidSdcKeys): IState {
    // Need to merge together the resolve blocks shared across all wizards with the resolves
    // needed specifically for this wizard.
    const stateObj = {
      template: `
      <regents-planning-wizard
        initial-wizard-state="$resolve.initialWizardState"
        current-school="$resolve.currentSchool"
        current-user="$resolve.currentUser"
        ng-if="!$ctrl.isTransfer"
      >
      </regents-planning-wizard>

      <regents-planning-wizard-transfer
        current-school="$resolve.currentSchool"
        current-user="$resolve.currentUser"
        initial-wizard-state="$resolve.initialWizardState"
        ng-if="$ctrl.isTransfer"
      >
      </regents-planning-wizard-transfer>`,
      controller(ImSchool: ImSchool, currentSchool) {
        this.isTransfer = ImSchool.isTransferSchool(currentSchool);
      },
      controllerAs: '$ctrl',
    };
    const genericWizardState = this.buildSharedWizardState(sdcKey);
    return Object.assign(stateObj, genericWizardState);
  }

  static buildRegentsPrepWizardState(sdcKey: TValidSdcKeys): IState {
    const stateDefaults = this.buildSharedWizardState(sdcKey);

    // Need to merge together the resolve blocks shared across all wizards with the resolves
    // needed specifically for this wizard.
    const stateObj = {
      template: `
        <regents-prep-wizard
          initial-wizard-state="$resolve.initialWizardState"
          student-supports="$resolve.studentSupports"
          current-school="$resolve.currentSchool"
          current-user="$resolve.currentUser"
          school-supports="$resolve.schoolSupports"
          ng-if="!$ctrl.isTransfer"
        >
        </regents-prep-wizard>

        <regents-prep-wizard-transfer
          initial-wizard-state="$resolve.initialWizardState"
          student-supports="$resolve.studentSupports"
          current-school="$resolve.currentSchool"
          current-user="$resolve.currentUser"
          school-supports="$resolve.schoolSupports"
          ng-if="$ctrl.isTransfer"
        >
        </regents-prep-wizard-transfer>
      `,
      resolve: Object.assign(
        {
          schoolSupports: [
            'Support',
            'currentSchool',
            'sdcActions',
            'UtilitiesService',
            (Support, currentSchool, sdcActions, UtilitiesService) => {
              /**
               * the regents-exams-supports-table component (on regents prep wizard) no longer relies on
               * binding to schoolSupports. The table is rendered from the store.
               * So perhaps we could get to a place where all the components that rely on schoolSupports
               * are getting them from the store, which would eliminate need to return the promise
               *
               * - JYR
               */
              const promise = Support.query({ schoolId: currentSchool._id }).$promise;
              return promise.then(data => {
                const supportsToStore = UtilitiesService.toPOJO(data);
                sdcActions.setAllSchoolSupports(supportsToStore);
                return data;
              });
            },
          ],
          studentSupports: [
            'currentSchool',
            'StudentSupport',
            'UtilitiesService',
            'sdcActions',
            (currentSchool: ISchool, StudentSupport, UtilitiesService, sdcActions) => {
              const promise = StudentSupport.get({ schoolId: currentSchool._id }).$promise;
              return promise.then(studentSupports => {
                const studentSupportsToStore = UtilitiesService.toPOJO(studentSupports);
                sdcActions.setAllStudentSupports(studentSupportsToStore.data);
                return studentSupports.data;
              });
            },
          ],
        },
        stateDefaults.resolve,
      ),
      controller(ImSchool: ImSchool, currentSchool) {
        this.isTransfer = ImSchool.isTransferSchool(currentSchool);
      },
      controllerAs: '$ctrl',
    };
    return Object.assign(stateDefaults, stateObj);
  }

  static buildCreditGapsWizardState(sdcKey: TValidSdcKeys): IState {
    const stateDefaults = this.buildSharedWizardState(sdcKey);
    const stateObj = {
      template: `
        <credit-gaps-transfer-wizard
          initial-wizard-state="$resolve.initialWizardState"
          current-school="$resolve.currentSchool"
          current-user="$resolve.currentUser"
          course-diffs="$resolve.currentCourseDiffs"
          ng-if="$ctrl.isTransfer">
        </credit-gaps-transfer-wizard>

        <credit-gaps-wizard
          initial-wizard-state="$resolve.initialWizardState"
          current-school="$resolve.currentSchool"
          current-user="$resolve.currentUser"
          current-course-diffs="$resolve.currentCourseDiffs"
          ng-if="!$ctrl.isTransfer">
        </credit-gaps-wizard>`,
      controller(ImSchool: ImSchool, currentSchool) {
        this.isTransfer = ImSchool.isTransferSchool(currentSchool);
      },
      controllerAs: '$ctrl',
      resolve: Object.assign(
        {
          currentCourseDiffs: [
            'CourseDiff',
            'currentSchool',
            (CourseDiff, currentSchool) => {
              const promise = CourseDiff.get({ schoolId: currentSchool._id }).$promise;
              return promise.then(diffs => {
                return diffs.data;
              });
            },
          ],
        },
        stateDefaults.resolve,
      ),
    };
    return Object.assign(stateDefaults, stateObj);
  }

  static buildSharedWizardState(sdcKey: TValidSdcKeys): IState {
    const stateType: TValidSdcViews = 'wizard';
    const sdcActivityKey = UtilitiesService.toUpperSnakeCase(sdcKey);

    return {
      url: '/wizard?filter&activeWizardStep',
      reloadOnSearch: false,
      resolve: {
        // This resolve block parses the query params. It shouldn't be injected into the controller; the controller
        // should use initialGridState instead.
        _wizardQueryParams($stateParams: IStateParamsService, $q: IQService): IPromise<any> {
          const query = { activeWizardStep: $q.resolve(null) };

          if ($stateParams.activeWizardStep) {
            query.activeWizardStep = $q.resolve($stateParams.activeWizardStep);
          }

          return $q.all(query);
        },

        // Resolves the initial wizard state. Takes into account the query params, existing values in the store, and the
        // default state for this SDC.
        /************************************************************
         *  NOTE: currentSchool MUST be injected here, even though
         *  it's not used directly in this resolve block because the
         *  getter for the SdcStateService[sdcActivityKey].filter requires
         *  a school is loaded. If this resolve doesn't inject the currentSchool
         *  it will run before the school is available.
         *
         *  TODO: Refactor SdcStateService[sdcActivityKey].filter to
         *  SdcStateService[sdcActivityKey].getFilter(school)
         *************************************************************/
        initialWizardState(
          _wizardQueryParams,
          SDCStore: ISdcStore,
          sdcActions: SdcActionsService,
          SdcStateService: SdcStateService,
          currentSchool,
        ): IWizState {
          const storeWizardState = SDCStore.getCurrentSdcState(sdcKey).wizard;
          const defaultWizardState = SdcStateService[sdcActivityKey];

          let userWizardStep = storeWizardState.activeWizardStep;

          const activeFilter = SdcStateBuilder._mergeFilters(
            defaultWizardState.filter,
            _wizardQueryParams.filter,
            storeWizardState.filter,
          );

          const activeWizardStep = SdcStateBuilder._mergeWizardSteps(
            _wizardQueryParams.activeWizardStep,
            storeWizardState.activeWizardStep,
          );

          sdcActions.setCurrentSdcState(sdcKey, stateType, 'filter', activeFilter);
          sdcActions.setCurrentSdcState(sdcKey, stateType, 'activeWizardStep', activeWizardStep);

          const ret = {
            filter: activeFilter,
            activeWizardStep: _wizardQueryParams.activeWizardStep || userWizardStep,
          };

          return ret;
        },
      },

      data: {
        roles: USER_ROLE_PERMISSIONS_FOR_STATES[`main.school.new-sdc.${sdcKey}.wizard`],
      },

      onEnter(
        initialWizardState: IWizState,
        sdcActions: SdcActionsService,
        currentSchool,
        SpinnerModal,
        SdcStateService,
      ) {
        const upperKey = UtilitiesService.toUpperSnakeCase(sdcKey);
        const activity = SdcStateService[upperKey];
        const filter = initialWizardState.filter;

        return sdcActions.initializeSdcActivityAsync({
          activity,
          filter,
          school: currentSchool,
        });
      },
    };
  }

  static _mergeFilters(_defaultFilter: IFilter, _queryParamFilter?: IFilter, _storeFilter?: IFilter): IFilter {
    // TODO: We may need to come up with a concept like "requiredFilter"
    // because default filter needs to be overwritten completely for wizards to remember
    // which user applied filter

    // empty objects should be considered undefined
    const defaultFilter = _.size(_defaultFilter) && _defaultFilter;
    const queryParamFilter = _.size(_queryParamFilter) && _queryParamFilter;
    const storeFilter = _.size(_storeFilter) && _storeFilter;

    return queryParamFilter || storeFilter || defaultFilter;
  }

  static _mergeSorts(
    _defaultSort: IColumnSort[],
    _queryParamSort?: IColumnSort[],
    _storeSort?: IColumnSort[],
  ): IColumnSort[] {
    // empty arrays should be considered undefined
    const queryParamSort = _.size(_queryParamSort) && _queryParamSort;
    const defaultSort = _.size(_defaultSort) && _defaultSort;
    const storeSort = _.size(_storeSort) && _storeSort;

    return queryParamSort || storeSort || defaultSort || [];
  }

  static _mergeGridViews(
    defaultGridView: TValidGridViews,
    queryParamGridView?: TValidGridViews,
    storeGridView?: TValidGridViews,
  ): TValidGridViews {
    return queryParamGridView || storeGridView || defaultGridView;
  }

  static _mergeWizardSteps(queryParamWizardStep?: number, storeWizardStep?: number): number {
    return queryParamWizardStep || storeWizardStep;
  }

  static _mergeUserAddedColumns(queryParamUserAddedCols, storeUserAddedCols) {
    return queryParamUserAddedCols || storeUserAddedCols;
  }
}
